import React, { useContext } from 'react';
import { Button } from 'react-bootstrap';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, startOfWeek, endOfWeek } from 'date-fns';
import { getAppointmentStatuses, formatDateForAPI } from '../../utils/appointmentUtils';
import { isCurrentMonth } from '../../utils/formatter';
import axiosInstance from '../../utils/axios';
import AuthContext from '../../context/AuthContext';

const StatusDots = ({ count, color }) => {
    const columns = Math.ceil(count / 6);
    const dotGroups = Array.from({ length: columns }, (_, i) =>
        [...Array(Math.min(6, count - i * 6))].map((_, j) => (
            <span
                key={`${i}-${j}`}
                className="rounded-circle d-block"
                style={{
                    width: '4px',
                    height: '4px',
                    backgroundColor: color,
                    marginBottom: '1px'
                }}
            />
        ))
    );

    return (
        <div className="d-flex gap-1">
            {dotGroups.map((dots, index) => (
                <div key={index} className="d-flex flex-column align-items-end">
                    {dots}
                </div>
            ))}
        </div>
    );
};

export function CalendarGrid({ currentDate, selectedDate, setSelectedButtonDate, appointments, setAppointments, onDateSelect }) {
    let { userDetails } = useContext(AuthContext)
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    /* Search data based on Date */
    const onPressCalendarButton = (oEvent, day) => {
        oEvent.preventDefault();
        onDateSelect(day);
        setSelectedButtonDate(day);

        if (day.getMonth() !== selectedDate.getMonth() || day.getFullYear() !== selectedDate.getFullYear()) {
            let firstDate = new Date(day.getFullYear(), day.getMonth(), 1);
            let endDate = new Date(day.getFullYear(), day.getMonth() + 1, 0);

            axiosInstance
                .get(`/appointments/`, {
                    params: {
                        appointment_start_date: formatDateForAPI(firstDate),
                        appointment_end_date: formatDateForAPI(endDate), 
                        clinicId: userDetails.clinic_id,
                    }
                })
                .then(response => {
                    if (response.status === 200) {
                        setAppointments(response.data.data)
                    }
                });
        }
    }

    return (
        <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center fw-medium text-muted small py-2">
                    {day}
                </div>
            ))}
            {days.map((day) => {
                const statusCounts = getAppointmentStatuses(day, appointments);
                const isOtherMonth = !isCurrentMonth(day, currentDate);

                return (
                    <Button
                        key={day.toString()}
                        onClick={oEvent => onPressCalendarButton(oEvent, day)}
                        variant={isSameDay(day, selectedDate) ? 'primary' : 'outline-secondary'}
                        className={`position-relative p-2 ${isOtherMonth ? 'text-muted' : ''}`}
                        style={{ height: '45px' }}
                    >
                        <span className="small">{format(day, 'd')}</span>
                        {statusCounts && (
                            <div
                                className="position-absolute bottom-0 end-0 d-flex gap-1 mb-1 me-1"
                                style={{ maxHeight: '35px' }}
                            >
                                <div><StatusDots count={statusCounts.Upcoming} color="#ffc107" /></div>
                                <div><StatusDots count={statusCounts.Completed} color="#34D058" /></div>
                                <div><StatusDots count={statusCounts.Missed} color="#dc3545" /></div>
                            </div>
                        )}
                    </Button>
                );
            })}
        </div>
    );
}